import React, { useContext } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  updateQuantityRequest,
  removeFromCartRequest,
} from "../../actions/ShopActions";
import {
  getImageAltText,
  sanitizeMarkup,
  getLineItemDiscount,
} from "../../utils/shop";
import { EVENT_PROPERTIES_VALUES } from "../../types/Amplitude";

// Components
import IconButton from "../Atoms/IconButton";
import Image from "../Atoms/Image";
import LoadingSpinner from "../Atoms/LoadingSpinner";
import QuantityPicker from "../Atoms/QuantityPicker";

// Utils
import { TrackingContext } from "../Utils/TrackingWrapper";
import { ImgixImage, ImgixPicture, ImgixSource } from "../Utils/ImgixImage";

// Constants
import { IMGIX_BASE_PATH } from "../../utils/constants";

// Stylesheets
import "./LineItem.scss";

export const decrementQuantity =
  ({
    checkoutId,
    lineItem,
    updateQuantity,
    trackDecreaseQuantityClick,
    trackRemoveItemFromCart,
    baseTrackingProperties,
  }) =>
  () => {
    // Note: Shopify already handles quantities of 0 or less for us.
    const updatedQuantity = lineItem.quantity - 1;

    trackRemoveItemFromCart({
      ...baseTrackingProperties,
      currentQuantity: 1,
      action: EVENT_PROPERTIES_VALUES.Decrease_Quantity_Icon_Clicked,
    });

    trackDecreaseQuantityClick({
      ...baseTrackingProperties,
      currentQuantity: lineItem.quantity,
      newQuantity: updatedQuantity,
      action: EVENT_PROPERTIES_VALUES.Decrease_Quantity_Icon_Clicked,
    });

    updateQuantity(checkoutId, lineItem.id, updatedQuantity);
  };

export const incrementQuantity =
  ({
    checkoutId,
    lineItem,
    updateQuantity,
    trackIncreaseQuantityClick,
    trackAddToShopifyCart,
    baseTrackingProperties,
  }) =>
  () => {
    const updatedQuantity = lineItem.quantity + 1;

    trackAddToShopifyCart({
      ...baseTrackingProperties,
      currentQuantity: lineItem.quantity,
      newQuantity: updatedQuantity,
      action: EVENT_PROPERTIES_VALUES.Increase_Quantity_Icon_Clicked,
    });

    trackIncreaseQuantityClick({
      ...baseTrackingProperties,
      currentQuantity: lineItem.quantity,
      newQuantity: updatedQuantity,
      action: EVENT_PROPERTIES_VALUES.Increase_Quantity_Icon_Clicked,
    });

    updateQuantity(checkoutId, lineItem.id, updatedQuantity);
  };

const renderLoadingSpinner = (lineItemId, quantityUpdates) => {
  if (quantityUpdates.includes(lineItemId)) {
    return <LoadingSpinner color="blue" />;
  }

  return null;
};

const onRemoveClick =
  ({
    removeFromCart,
    checkoutId,
    lineItemId,
    variantId,
    trackRemoveItemFromCart,
    quantity,
    baseTrackingProperties,
  }) =>
  () => {
    removeFromCart(checkoutId, lineItemId, variantId);

    trackRemoveItemFromCart({
      ...baseTrackingProperties,
      currentQuantity: quantity,
      action: EVENT_PROPERTIES_VALUES.Remove_Item_Icon_Clicked,
    });
  };

export const renderPromotionalMessage = (message) => {
  if (message) {
    return (
      <p className="caption_primary ship_date promotional_message">{message}</p>
    );
  }

  return null;
};

export const renderDescription = (description) => {
  // If item is a variant, render the description which contains the variant name
  if (description) {
    return (
      <span
        className="caption_secondary variant_info"
        dangerouslySetInnerHTML={{ __html: sanitizeMarkup(description) }}
      />
    );
  }

  return null;
};

export function getLineItemPrice(lineItem) {
  const [discount] = getLineItemDiscount(lineItem);
  const originalTotal = lineItem.quantity * lineItem.price;

  if (!discount) {
    return originalTotal.toFixed(2);
  }

  const discountVal = parseFloat(discount.discountApplication.value.amount);
  const discountPercentage =
    parseFloat(discount.discountApplication.value.percentage) * 0.01;

  if (discountVal) {
    return (originalTotal - discountVal).toFixed(2);
  } else if (discountPercentage) {
    //.01 is accounting for rounding errors
    let calculateDiscount = (discountPercentage * originalTotal - 0.01).toFixed(
      2
    );
    return (originalTotal - calculateDiscount).toFixed(2);
  } else {
    console.log("discount exists but does not have a value or percentage");
    return originalTotal;
  }
}

export const LineItem = ({
  lineItem,
  checkout,
  removeFromCart,
  updateQuantity,
  quantityUpdates,
}) => {
  const {
    trackingActions: {
      trackAddToShopifyCart,
      trackRemoveItemFromCart,
      trackDecreaseQuantityClick,
      trackIncreaseQuantityClick,
    },
    pageUrl,
  } = useContext(TrackingContext);
  const baseTrackingProperties = {
    product: lineItem.productName,
    variant: lineItem.variantName,
    sectionName: EVENT_PROPERTIES_VALUES.Cart,
    pageUrl,
    lineItem,
  };
  const altText = getImageAltText({
    altText: lineItem.image.altText,
    productName: lineItem.productName,
    variantName: lineItem.variantName,
  });

  return (
    <>
      <li className="line_item">
        {renderLoadingSpinner(lineItem.id, quantityUpdates)}
        <div className="product_image_container">
          {lineItem.handle === "whoop-restore" ? (
            <ImgixPicture
              fallback={
                <ImgixImage
                  src="hatch-sleep-membership-logo.png"
                  alt="hatch sleep membership logo"
                />
              }
            >
              <ImgixSource src="hatch-sleep-membership-logo.png" width={134} />
            </ImgixPicture>
          ) : (
            <ImgixImage
              src={lineItem.image.src}
              alt={altText}
              width={96}
              height={96}
              isShopify
              className="product_image"
            />
          )}
        </div>

        <div className="line_item_content">
          <div className="product_info">
            <header className="header">
              <h3 className="h6_text product_title">
                {lineItem.handle === "whoop-restore"
                  ? "Hatch Sleep Membership"
                  : lineItem.displayName}
              </h3>
              {lineItem.handle === "whoop-restore" ? (
                <p className="paragraph description body_secondary">
                  Included in $14.99/mo membership Limit 1 per order
                </p>
              ) : null}

              {renderDescription(lineItem.description)}

              {renderPromotionalMessage(lineItem.promotionalMessage)}
            </header>

            <IconButton
              className="item_remove_button"
              ariaLabel={`Remove ${lineItem.displayName} from cart`}
              onClick={onRemoveClick({
                checkoutId: checkout.id,
                lineItemId: lineItem.id,
                variantId: lineItem.variantId,
                removeFromCart,
                trackRemoveItemFromCart,
                quantity: lineItem.quantity,
                baseTrackingProperties,
              })}
            >
              <Image
                src={`${IMGIX_BASE_PATH}/remove-item.svg`}
                alt="Remove item"
              />
            </IconButton>
          </div>

          <footer className="line_item_footer">
            {lineItem.handle === "whoop-restore" ? (
              <div></div>
            ) : (
              <div className="quantity_actions">
                <QuantityPicker
                  quantity={lineItem.quantity}
                  productName={lineItem.displayName}
                  handleIncrement={incrementQuantity({
                    checkoutId: checkout.id,
                    lineItem,
                    updateQuantity,
                    trackIncreaseQuantityClick,
                    trackAddToShopifyCart,
                    baseTrackingProperties,
                  })}
                  handleDecrement={decrementQuantity({
                    checkoutId: checkout.id,
                    lineItem,
                    updateQuantity,
                    trackDecreaseQuantityClick,
                    trackRemoveItemFromCart,
                    baseTrackingProperties,
                  })}
                />
              </div>
            )}
            <span className="h6_text price" data-testid="price">
              ${getLineItemPrice(lineItem)}
              {lineItem.handle === "whoop-restore" ? "/mo" : ""}
            </span>
          </footer>
        </div>
      </li>
      {lineItem.handle === "whoop-restore" ? (
        <li className="line_item">
          {renderLoadingSpinner(lineItem.id, quantityUpdates)}
          <div className="product_image_container">
            <ImgixPicture
              fallback={
                <ImgixImage
                  src="Whoop-Restore-With-Membership.png"
                  alt="Restore Product"
                />
              }
            >
              <ImgixSource
                src="Whoop-Restore-With-Membership.png"
                width={134}
              />
            </ImgixPicture>
          </div>

          <div className="line_item_content">
            <div className="product_info">
              <header className="header">
                <h3 className="h6_text product_title">Restore</h3>
                <p className="paragraph description body_secondary">
                  Included in $14.99/mo membership Limit 1 per order
                </p>
              </header>
            </div>

            <footer className="line_item_footer">
              <div></div>
              <span className="h6_text price" data-testid="price">
                Free
              </span>
            </footer>
          </div>
        </li>
      ) : (
        ""
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    checkout: state.shop.checkout,
    quantityUpdates: state.shop.quantityUpdates || [],
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateQuantity: (checkoutId, lineItemId, quantity) =>
      dispatch(updateQuantityRequest(checkoutId, lineItemId, quantity)),
    removeFromCart: (checkoutId, lineItemId, variantId) =>
      dispatch(removeFromCartRequest(checkoutId, lineItemId, variantId)),
  };
};

LineItem.propTypes = {
  lineItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    variantId: PropTypes.string.isRequired,
    description: PropTypes.string,
    promotionalMessage: PropTypes.string,
    variantName: PropTypes.string,
    quantity: PropTypes.number.isRequired,
    productName: PropTypes.string.isRequired,
    image: PropTypes.object.isRequired,
    displayName: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      .isRequired,
  }).isRequired,
  checkout: PropTypes.object.isRequired,
  removeFromCart: PropTypes.func.isRequired,
  updateQuantity: PropTypes.func.isRequired,
  quantityUpdates: PropTypes.array,
};

export default connect(mapStateToProps, mapDispatchToProps)(LineItem);
